import React, { useEffect, useState, useRef } from 'react';
import taskAPI from '../api/taskAPI';
import UserDashboard from './UserDashboard';
import {
  FaUserPlus, FaTimes, FaChevronRight, FaLink,
  FaUpload, FaFilePdf, FaFileWord, FaFileImage,
  FaFile, FaDownload, FaEye, FaSpinner, FaTrash,
  FaFileAlt,
} from 'react-icons/fa';

// ── Role badge colours ────────────────────────────────────────────────────────
const ROLE_COLOR = {
  'Marketing':     'bg-pink-100 text-pink-700',
  'Project Leads': 'bg-indigo-100 text-indigo-700',
  'HR & Design':   'bg-teal-100 text-teal-700',
  'TL':            'bg-blue-100 text-blue-700',
  'Developer':     'bg-green-100 text-green-700',
  'Office':        'bg-gray-100 text-gray-600',
};
const RoleBadge = ({ role }) => (
  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ROLE_COLOR[role] || 'bg-gray-100 text-gray-600'}`}>
    {role}
  </span>
);

// ── Status badge (read-only) ──────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
  const styles =
    status === 'Completed'   ? 'bg-green-100 text-green-700' :
    status === 'In Progress' ? 'bg-blue-100 text-blue-700'   :
                               'bg-yellow-100 text-yellow-700';
  return (
    <span className={`text-xs font-semibold px-2 py-1 rounded-full ${styles}`}>
      {status}
    </span>
  );
};

// ── Assignment chain trail ────────────────────────────────────────────────────
const ChainTrail = ({ chain }) => {
  if (!chain?.length) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1">
      {chain.map((entry, i) => (
        <React.Fragment key={i}>
          <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 rounded-full">
            {entry}
          </span>
          {i < chain.length - 1 && <FaChevronRight className="text-purple-300 text-xs" />}
        </React.Fragment>
      ))}
    </div>
  );
};

// ── File helpers ──────────────────────────────────────────────────────────────
const getFileType = (file) => {
  if (file.type.startsWith('image/'))  return 'image';
  if (file.type === 'application/pdf') return 'pdf';
  if (file.type.includes('word') || file.name.endsWith('.doc') || file.name.endsWith('.docx')) return 'doc';
  return 'other';
};
const FileIcon = ({ fileType }) => {
  if (fileType === 'image') return <FaFileImage className="text-blue-500 text-xl shrink-0" />;
  if (fileType === 'pdf')   return <FaFilePdf   className="text-red-500 text-xl shrink-0" />;
  if (fileType === 'doc')   return <FaFileWord  className="text-blue-700 text-xl shrink-0" />;
  return <FaFile className="text-gray-500 text-xl shrink-0" />;
};
const formatSize = (b) =>
  b < 1024 ? `${b}B` : b < 1048576 ? `${(b / 1024).toFixed(1)}KB` : `${(b / 1048576).toFixed(1)}MB`;
const toBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

// ── FileUploadPanel (task attachments) ───────────────────────────────────────
const FileUploadPanel = ({ taskId, existingFiles = [], onUploaded }) => {
  const [pending, setPending]       = useState([]);
  const [uploading, setUploading]   = useState(false);
  const [uploaded, setUploaded]     = useState(existingFiles);
  const [previewUrl, setPreviewUrl] = useState(null);
  const inputRef = useRef(null);

  const addFiles = (fileList) => {
    const mapped = Array.from(fileList).map(f => ({
      file: f, name: f.name, size: f.size,
      fileType: getFileType(f),
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    setPending(prev => [...prev, ...mapped]);
  };

  const handleUpload = async () => {
    if (!taskId)         { alert('Task ID missing.');    return; }
    if (!pending.length) { alert('No files selected.'); return; }
    setUploading(true);
    try {
      const files = await Promise.all(
        pending.map(async pf => ({ base64: await toBase64(pf.file), name: pf.name, fileType: pf.fileType }))
      );
      const res = await taskAPI.post(`/${taskId}/attachments`, { files });
      setUploaded(res.data.attachments);
      setPending([]);
      onUploaded?.(res.data.attachments);
      alert(`✅ ${files.length} file(s) uploaded!`);
    } catch (err) { console.error(err); alert('❌ Upload failed.'); }
    finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await taskAPI.delete(`/${taskId}/attachments/${id}`);
      setUploaded(prev => prev.filter(f => f._id !== id));
    } catch { alert('❌ Delete failed.'); }
  };

  return (
    <div className="space-y-3">
      <div onClick={() => inputRef.current?.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); addFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center cursor-pointer hover:bg-purple-50 transition">
        <FaUpload className="mx-auto text-purple-400 text-xl mb-1" />
        <p className="text-sm text-gray-500">Click or drag & drop files here</p>
        <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, PDF, DOC, DOCX</p>
        <input ref={inputRef} type="file" multiple
          accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} className="hidden" />
      </div>
      {pending.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Ready to upload</p>
          {pending.map((pf, i) => (
            <div key={i} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              {pf.preview ? <img src={pf.preview} alt="" className="w-9 h-9 object-cover rounded" /> : <FileIcon fileType={pf.fileType} />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{pf.name}</p>
                <p className="text-xs text-gray-400">{formatSize(pf.size)}</p>
              </div>
              <button onClick={() => setPending(prev => prev.filter((_, j) => j !== i))}
                className="text-red-400 hover:text-red-600"><FaTrash /></button>
            </div>
          ))}
          <button onClick={handleUpload} disabled={uploading}
            className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white font-semibold py-2 rounded-lg flex items-center justify-center gap-2">
            {uploading ? <><FaSpinner className="animate-spin" /> Uploading...</> : <><FaUpload /> Upload {pending.length} file(s)</>}
          </button>
        </div>
      )}
      {uploaded.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Uploaded ({uploaded.length})</p>
          {uploaded.map(f => (
            <div key={f._id} className="flex items-center gap-3 bg-white border rounded-lg px-3 py-2">
              <FileIcon fileType={f.fileType} />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">{f.name}</p>
                <p className="text-xs text-gray-400">by {f.uploadedBy} · {new Date(f.uploadedAt).toLocaleDateString('en-GB')}</p>
              </div>
              <div className="flex items-center gap-2">
                {f.fileType === 'image' && <button onClick={() => setPreviewUrl(f.url)} className="text-blue-500 hover:text-blue-700"><FaEye /></button>}
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="text-green-500 hover:text-green-700"><FaDownload /></a>
                <button onClick={() => handleDelete(f._id)} className="text-red-400 hover:text-red-600"><FaTrash /></button>
              </div>
            </div>
          ))}
        </div>
      )}
      {!uploaded.length && !pending.length && <p className="text-xs text-gray-400 text-center">No attachments yet.</p>}
      {previewUrl && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-80 flex items-center justify-center px-4" onClick={() => setPreviewUrl(null)}>
          <div className="relative max-w-3xl w-full">
            <button onClick={() => setPreviewUrl(null)} className="absolute -top-10 right-0 text-white text-2xl">✕</button>
            <img src={previewUrl} alt="preview" className="w-full rounded-xl shadow-2xl" />
          </div>
        </div>
      )}
    </div>
  );
};

// ── Roles that can forward tasks ──────────────────────────────────────────────
const CAN_FORWARD = ['Office', 'HR & Design', 'Marketing', 'Project Leads', 'TL'];

// ─────────────────────────────────────────────────────────────────────────────
const WorkerTaskReport = () => {
  const worker     = JSON.parse(localStorage.getItem('worker'));
  const workerId   = worker?._id;
  const workerRole = worker?.role;

  const [tasks, setTasks]               = useState([]);
  const [searchTerm, setSearchTerm]     = useState('');
  const [statusFilter, setStatusFilter] = useState('All');

  // ── Report modal (file upload instead of webcam) ──────────────────────────
  const [showReportModal, setShowReportModal]       = useState(false);
  const [selectedTask, setSelectedTask]             = useState(null);
  const [reportDescription, setReportDescription]   = useState('');
  const [reportFile, setReportFile]                 = useState(null);        // File object
  const [reportFilePreview, setReportFilePreview]   = useState(null);        // preview URL
  const [reportFileType, setReportFileType]         = useState('');
  const [submittingReport, setSubmittingReport]     = useState(false);
  const reportInputRef = useRef(null);

  // ── Forward modal ─────────────────────────────────────────────────────────
  const [showForwardModal, setShowForwardModal]     = useState(false);
  const [forwardTask, setForwardTask]               = useState(null);
  const [assignableWorkers, setAssignableWorkers]   = useState([]);
  const [forwardSelectedIds, setForwardSelectedIds] = useState([]);

  // ── Chain modal ───────────────────────────────────────────────────────────
  const [showChainModal, setShowChainModal] = useState(false);
  const [chainTask, setChainTask]           = useState(null);

  // ── Attachment modal ──────────────────────────────────────────────────────
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [attachTask, setAttachTask]           = useState(null);

  useEffect(() => { if (workerId) fetchTasks(); }, [workerId]);

  const fetchTasks = async () => {
    try {
      const [legacyRes, chainRes] = await Promise.allSettled([
        taskAPI.get(`/worker/${workerId}`),
        taskAPI.get('/my-tasks'),
      ]);
      const legacy = legacyRes.status === 'fulfilled' ? legacyRes.value.data : [];
      const chain  = chainRes.status  === 'fulfilled' ? chainRes.value.data  : [];
      const merged = [...chain];
      legacy.forEach(t => { if (!merged.find(m => m._id === t._id)) merged.push(t); });
      merged.sort((a, b) => new Date(b.createdAt || b._id) - new Date(a.createdAt || a._id));
      setTasks(merged);
    } catch (err) { console.error('fetchTasks error:', err); }
  };

  // ── Report modal handlers ─────────────────────────────────────────────────
  const openReportModal = (task) => {
    setSelectedTask(task);
    setReportDescription('');
    setReportFile(null);
    setReportFilePreview(null);
    setReportFileType('');
    setShowReportModal(true);
  };

  const handleReportFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ftype = getFileType(file);
    setReportFile(file);
    setReportFileType(ftype);
    setReportFilePreview(ftype === 'image' ? URL.createObjectURL(file) : null);
    e.target.value = '';
  };

  const submitTaskReport = async () => {
    if (!reportDescription) { alert('Please enter a description.'); return; }
    if (!reportFile)         { alert('Please choose a file to attach.'); return; }
    setSubmittingReport(true);
    try {
      const base64 = await toBase64(reportFile);
      await taskAPI.post('/submit', {
        taskId:      selectedTask._id,
        description: reportDescription,
        photo:       base64,                    // backend expects 'photo' field
        submittedBy: worker?.username,          // who submitted
        submittedById: workerId,
      });
      alert('✅ Report submitted!');
      setShowReportModal(false);
    } catch (err) {
      console.error(err);
      alert('❌ Failed to submit report.');
    } finally { setSubmittingReport(false); }
  };

  // ── Forward handlers ──────────────────────────────────────────────────────
  const openForwardModal = async (task) => {
    setForwardTask(task); setForwardSelectedIds([]);
    try {
      const res = await taskAPI.get('/assignable-workers');
      const existingIds = (task.assignees || []).map(a => a.userId?.toString());
      setAssignableWorkers(res.data.filter(w => !existingIds.includes(w._id)));
    } catch (err) { console.error(err); }
    setShowForwardModal(true);
  };

  const handleForward = async () => {
    if (!forwardSelectedIds.length) { alert('Select at least one worker.'); return; }
    try {
      await taskAPI.post(`/${forwardTask._id}/forward`, { assigneeIds: forwardSelectedIds });
      alert('✅ Task forwarded!'); setShowForwardModal(false); fetchTasks();
    } catch (err) { alert(err.response?.data?.error || '❌ Forward failed.'); }
  };

  const toggleForward = (id) =>
    setForwardSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const filtered = tasks.filter(t => {
    const matchSearch =
      t.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.today?.includes(searchTerm) || t.deadline?.includes(searchTerm);
    return matchSearch && (statusFilter === 'All' || t.status === statusFilter);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-6 pt-24">
      <UserDashboard />

      <div className="max-w-7xl mx-auto bg-white p-6 rounded-xl shadow-lg">

        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-5 gap-4">
          <div>
            <h2 className="text-3xl font-bold font-mono text-purple-700">Task Report</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Logged in as <span className="font-semibold text-purple-600">{worker?.username}</span>{' '}
              <RoleBadge role={workerRole} />
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {['All', 'Pending', 'In Progress', 'Completed'].map(s => (
              <button key={s} onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${
                  statusFilter === s
                    ? 'bg-purple-600 text-white border-purple-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-purple-400'
                }`}>{s}</button>
            ))}
          </div>
        </div>

        <input type="text" placeholder="Search by title or date..."
          value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="border px-4 py-2 rounded-lg w-full md:w-96 mb-4" />

        {/* Table */}
        <div className="max-h-[500px] overflow-auto">
          <table className="min-w-full table-auto text-sm">
            <thead className="bg-purple-100 text-purple-700 sticky top-0 z-10">
              <tr>
                <th className="py-3 px-4 text-left">Date</th>
                <th className="py-3 px-4 text-left">Deadline</th>
                <th className="py-3 px-4 text-left">Title</th>
                <th className="py-3 px-4 text-left">Description</th>
                <th className="py-3 px-4 text-left">Status</th>
                <th className="py-3 px-4 text-left">Files</th>
                <th className="py-3 px-4 text-left">Chain</th>
                <th className="py-3 px-4 text-left">Actions</th>
              </tr>
            </thead>
            <tbody className="text-gray-700">
              {filtered.map((task) => (
                <tr key={task._id} className="border-t hover:bg-purple-50 transition">

                  {/* Date */}
                  <td className="py-2 px-4 whitespace-nowrap text-xs">
                    {task.today
                      ? new Date(task.today).toLocaleDateString('en-GB')
                      : new Date(task.createdAt).toLocaleDateString('en-GB')}
                  </td>

                  {/* Deadline */}
                  <td className="py-2 px-4 whitespace-nowrap text-xs">
                    {task.deadline ? new Date(task.deadline).toLocaleDateString('en-GB') : '—'}
                  </td>

                  {/* Title */}
                  <td className="py-2 px-4 font-medium text-gray-800">{task.title}</td>

                  {/* Description — READ ONLY (no editable input) */}
                  <td className="py-2 px-4 text-gray-600 max-w-[200px]">
                    <p className="text-xs line-clamp-2">{task.description || '—'}</p>
                  </td>

                  {/* Status — READ Only badge (only admin can change) */}
                  <td className="py-2 px-4">
                    <StatusBadge status={task.status} />
                  </td>

                  {/* Files */}
                  <td className="py-2 px-4">
                    <button onClick={() => { setAttachTask(task); setShowAttachModal(true); }}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
                      <FaUpload />
                      {task.attachments?.length > 0 ? `${task.attachments.length} file(s)` : 'Files'}
                    </button>
                  </td>

                  {/* Chain */}
                  <td className="py-2 px-4">
                    {task.assignmentChain?.length > 0 ? (
                      <button onClick={() => { setChainTask(task); setShowChainModal(true); }}
                        className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-800 font-medium">
                        <FaLink /> View
                      </button>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </td>

                  {/* Actions */}
                  <td className="py-2 px-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button onClick={() => openReportModal(task)}
                        className="flex items-center gap-1 bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded-lg transition whitespace-nowrap">
                        <FaFileAlt /> Report
                      </button>
                      {CAN_FORWARD.includes(workerRole) && task.status !== 'Completed' && (
                        <button onClick={() => openForwardModal(task)}
                          className="flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white text-xs px-3 py-1.5 rounded-lg transition whitespace-nowrap">
                          <FaUserPlus /> Forward
                        </button>
                      )}
                    </div>
                  </td>

                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan="8" className="text-center py-8 text-gray-400">No tasks found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── ATTACHMENT MODAL ─────────────────────────────────────────────────── */}
      {showAttachModal && attachTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl p-6 relative max-h-[90vh] overflow-y-auto">
            <button onClick={() => setShowAttachModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Files</h3>
            <p className="text-sm text-gray-500 mb-1">"<span className="font-medium text-gray-700">{attachTask.title}</span>"</p>
            <p className="text-xs text-gray-400 mb-4">📎 Shared with everyone assigned to this task.</p>
            <FileUploadPanel taskId={attachTask._id} existingFiles={attachTask.attachments || []}
              onUploaded={(attachments) => {
                setTasks(prev => prev.map(t => t._id === attachTask._id ? { ...t, attachments } : t));
                setAttachTask(prev => ({ ...prev, attachments }));
              }} />
          </div>
        </div>
      )}

      {/* ── REPORT MODAL (file upload, no webcam) ────────────────────────────── */}
      {showReportModal && selectedTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={() => setShowReportModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>

            <h2 className="text-xl font-bold text-purple-700 mb-1 text-center">Submit Report</h2>
            <p className="text-sm text-gray-500 text-center mb-4">
              "{selectedTask.title}"
            </p>

            {/* Submitted by info */}
            <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 mb-4">
              <span className="text-xs text-gray-500">Submitted by:</span>
              <span className="text-xs font-semibold text-purple-700">{worker?.username}</span>
              <RoleBadge role={workerRole} />
            </div>

            {/* Description */}
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              placeholder="Describe what was done..."
              value={reportDescription}
              onChange={e => setReportDescription(e.target.value)}
              className="border rounded-lg w-full p-3 mb-4 focus:ring-2 focus:ring-purple-500 outline-none"
              rows="4"
            />

            {/* File upload (replaces webcam) */}
            <label className="block text-sm font-medium text-gray-700 mb-2">Attach File / Photo</label>
            <div
              onClick={() => reportInputRef.current?.click()}
              className="border-2 border-dashed border-purple-300 rounded-lg p-4 text-center cursor-pointer hover:bg-purple-50 transition mb-3"
            >
              <FaUpload className="mx-auto text-purple-400 text-xl mb-1" />
              <p className="text-sm text-gray-500">
                {reportFile ? reportFile.name : 'Click to choose a file'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">JPG, PNG, PDF, DOC, DOCX</p>
              <input
                ref={reportInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={handleReportFileChange}
                className="hidden"
              />
            </div>

            {/* File preview */}
            {reportFile && (
              <div className="flex items-center gap-3 bg-gray-50 border rounded-lg px-3 py-2 mb-4">
                {reportFilePreview
                  ? <img src={reportFilePreview} alt="preview" className="w-12 h-12 object-cover rounded" />
                  : <FileIcon fileType={reportFileType} />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{reportFile.name}</p>
                  <p className="text-xs text-gray-400">{formatSize(reportFile.size)}</p>
                </div>
                <button onClick={() => { setReportFile(null); setReportFilePreview(null); }}
                  className="text-red-400 hover:text-red-600"><FaTrash /></button>
              </div>
            )}

            <button onClick={submitTaskReport} disabled={submittingReport}
              className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition">
              {submittingReport
                ? <><FaSpinner className="animate-spin" /> Submitting...</>
                : <><FaFileAlt /> Submit Report</>}
            </button>
          </div>
        </div>
      )}

      {/* ── FORWARD MODAL ────────────────────────────────────────────────────── */}
      {showForwardModal && forwardTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={() => setShowForwardModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Forward Task</h3>
            <p className="text-sm text-gray-500 mb-3">"<span className="font-medium text-gray-700">{forwardTask.title}</span>"</p>
            {forwardTask.assignmentChain?.length > 0 && (
              <div className="mb-4 p-3 bg-purple-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Current chain:</p>
                <ChainTrail chain={forwardTask.assignmentChain} />
                <div className="flex items-center gap-1 mt-1">
                  <FaChevronRight className="text-purple-300 text-xs" />
                  <span className="text-xs bg-purple-100 text-purple-700 border border-purple-300 px-2 py-0.5 rounded-full font-semibold">
                    {worker?.username} ({workerRole}) → ?
                  </span>
                </div>
              </div>
            )}
            <div className="border rounded-lg divide-y max-h-56 overflow-y-auto mb-4">
              {assignableWorkers.length === 0
                ? <p className="text-sm text-gray-400 p-4 text-center">No eligible workers to forward to.</p>
                : assignableWorkers.map(w => (
                  <label key={w._id}
                    className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-purple-50 transition ${forwardSelectedIds.includes(w._id) ? 'bg-purple-50' : ''}`}>
                    <input type="checkbox" checked={forwardSelectedIds.includes(w._id)}
                      onChange={() => toggleForward(w._id)} className="accent-purple-600" />
                    <span className="flex-1 text-sm font-medium text-gray-700">{w.username}</span>
                    <RoleBadge role={w.role} />
                    {w.team && <span className="text-xs text-gray-400">{w.team}</span>}
                  </label>
                ))}
            </div>
            {forwardSelectedIds.length > 0 && (
              <p className="text-xs text-purple-600 mb-3">{forwardSelectedIds.length} worker(s) selected</p>
            )}
            <button onClick={handleForward}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 rounded-lg transition">
              Forward Task
            </button>
          </div>
        </div>
      )}

      {/* ── CHAIN DETAIL MODAL ───────────────────────────────────────────────── */}
      {showChainModal && chainTask && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl p-6 relative">
            <button onClick={() => setShowChainModal(false)} className="absolute top-3 right-3 text-gray-500 hover:text-red-500 text-xl"><FaTimes /></button>
            <h3 className="text-lg font-bold text-purple-700 mb-1">Assignment Chain</h3>
            <p className="text-sm text-gray-500 mb-4">"<span className="font-medium text-gray-700">{chainTask.title}</span>"</p>
            <div className="mb-4">
              <p className="text-xs text-gray-400 mb-2">Delegation path:</p>
              <ChainTrail chain={chainTask.assignmentChain} />
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-2">All people on this task:</p>
              <div className="space-y-2">
                {chainTask.assignees?.map((a, i) => (
                  <div key={i} className={`flex items-center gap-2 p-2 rounded-lg border ${
                    a.userId?.toString() === workerId ? 'bg-purple-50 border-purple-300' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <span className="text-sm font-medium text-gray-700 flex-1">{a.username}</span>
                    <RoleBadge role={a.role} />
                    <span className="text-xs text-gray-400">← {a.assignedBy}</span>
                    {a.userId?.toString() === workerId && (
                      <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">You</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkerTaskReport;